import { useMutation, useQueryClient } from "@tanstack/react-query"
import { assertWithErrorCode } from "@krakentech/error-intl"
import { ERROR_CODE } from "@/constants/errorCode"
import { type InputOf, OEBackend } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { useDataLayer } from "@/lib/dataLayer"
import { logger } from "@/lib/logger"
import { EcoSessionDataLayerEvent } from "../datalayer/types/ecoSessionDataLayerEvent"

const EnrollEcoSessionMutation = OEBackend.graphql(`
    mutation EnrollEcoSession($input: EnrollEcoSessionInput!) {
        enrollEcoSession(input: $input) {
            subscription {
                accountNumber
            }
        }
    }
`)

const UnenrollEcoSessionMutation = OEBackend.graphql(`
    mutation UnenrollEcoSession($input: UnenrollEcoSessionInput!) {
        unenrollEcoSession(input: $input) {
            subscription {
                accountNumber
            }
        }
    }
`)

type UseEnrollmentEcoSessionProps = {
    path: "dashboard" | "anonymous"
}

export function useEnrollmentEcoSession(props?: UseEnrollmentEcoSessionProps) {
    const oeGraphQLClient = useGraphQLClient({ target: "oeBackend" })
    const queryClient = useQueryClient()
    const datalayer = useDataLayer()

    const enroll = useMutation({
        mutationKey: ["enrollEcoSession"],
        mutationFn: async (input: InputOf<typeof EnrollEcoSessionMutation>) => {
            const data = await oeGraphQLClient.request(EnrollEcoSessionMutation, {
                input,
            })

            assertWithErrorCode(
                data.enrollEcoSession?.subscription.accountNumber,
                ERROR_CODE.UNKNOWN_ERROR
            )

            return data.enrollEcoSession.subscription.accountNumber
        },
        onSuccess: async () => {
            datalayer.sendDataLayerEvent(EcoSessionDataLayerEvent.EnrollmentSuccess, {
                path: props?.path ?? "dashboard",
            })

            await queryClient.invalidateQueries({
                queryKey: ["EcoSessionPageQueryOeBackend"],
            })

            await queryClient.invalidateQueries({
                queryKey: ["EcoSessionActiveProgram"],
            })

            await queryClient.invalidateQueries({
                queryKey: ["EcoSessionActiveSubscription"],
            })
        },
        onError: (error) => {
            logger.error({
                error,
                meta: {
                    feature: "ecoSession",
                    source: "useEnrollmentEcoSession - enroll",
                },
            })
        },
    })

    const unenroll = useMutation({
        mutationKey: ["unenrollEcoSession"],
        mutationFn: async (input: InputOf<typeof UnenrollEcoSessionMutation>) => {
            const data = await oeGraphQLClient.request(UnenrollEcoSessionMutation, {
                input,
            })

            assertWithErrorCode(
                data.unenrollEcoSession?.subscription.accountNumber,
                ERROR_CODE.UNKNOWN_ERROR
            )

            return data.unenrollEcoSession.subscription.accountNumber
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["EcoSessionPageQueryOeBackend"],
            })

            await queryClient.invalidateQueries({
                queryKey: ["EcoSessionActiveProgram"],
            })

            await queryClient.invalidateQueries({
                queryKey: ["EcoSessionActiveSubscription"],
            })
        },
        onError: (error) => {
            logger.error({
                error,
                meta: {
                    feature: "ecoSession",
                    source: "useEnrollmentEcoSession - unenroll",
                },
            })
        },
    })

    return { enroll, unenroll }
}
